import { FC } from 'react'
import Link from 'next/link'
import cx from 'classnames'
import { useTranslation } from 'next-i18next'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { Document } from '@contentful/rich-text-types'

import { useCustomer } from '../services/customer/loadAttributes'

import { InstagramIcon, LinkedInIcon } from 'icons'
import useAuth from '../hooks/useAuth'
import { useTracking } from '../services/context/TrackingContext'
import { useMediaQuery } from 'usehooks-ts'

const FooterHeading: FC<{ className?: string }> = ({ children, className }) => {
  return (
    <h3 className={cx(className, 'pb-12 pt-14 text-14 font-bold text-white')}>
      {children}
    </h3>
  )
}

const FooterList: FC<{ className?: string }> = ({ children, className }) => {
  return (
    <ul
      className={cx(
        className,
        'space-y-10 border-t border-teal-4 pb-24 pt-10 md:pb-32'
      )}
    >
      {children}
    </ul>
  )
}

const FooterListItem: FC<{
  href?: string
  className?: string
  label?: string
  trackingPosition: string
  trackingPriority: number
}> = ({
  children,
  className,
  href,
  label,
  trackingPosition,
  trackingPriority,
}) => {
  const { track } = useTracking()

  return (
    <li
      className={cx({
        'flex items-center': label,
      })}
    >
      {!href ? (
        <span className={cx(className, 'text-14 text-[#EBEAE7] opacity-70')}>
          {children}
        </span>
      ) : (
        <Link href={href} passHref>
          <a
            className={cx(className, 'block text-14 text-white underline')}
            onClick={() => {
              track({
                event: 'CTA Clicked',
                properties: {
                  link_type: 'Text',
                  link_location: 'Footer',
                  link_text: children,
                  link_position: trackingPosition,
                  link_priority: trackingPriority + 1,
                },
              })
            }}
          >
            {children}
          </a>
        </Link>
      )}
      {label ? (
        <span className="ml-16 block rounded-full bg-gold-1 px-8 py-4 text-8 font-bold uppercase tracking-1 text-teal-5">
          {label}
        </span>
      ) : null}
    </li>
  )
}

export interface IFooterLink {
  key: string
  value: string
}

export interface IFooterProps {
  footerSiteLinks?: IFooterLink[]
  footerCurrentLocations?: IFooterLink[]
  footerFutureLocations?: string[]
  footerAdditionalLinksHeading?: string
  footerAdditionalLinks?: IFooterLink[]
  footerGuidesLinks?: IFooterLink[]
  footerConnectText?: {
    json: Document
  }
}

const Footer = ({
  footerSiteLinks,
  footerCurrentLocations,
  footerFutureLocations,
  footerAdditionalLinksHeading,
  footerAdditionalLinks,
  footerGuidesLinks,
  footerConnectText,
}: IFooterProps) => {
  const { t } = useTranslation('common')
  const { isAuthenticated } = useAuth()
  const { data: customer } = useCustomer()

  const isDesktop = useMediaQuery('(min-width: 48em)') // i.e. md

  const showAdditionalLinks =
    isAuthenticated &&
    footerAdditionalLinks?.length &&
    (!customer?.approval.status ||
      customer?.approval.status === 'created' ||
      customer?.approval.status === 'failed')

  const guidesLinks = footerGuidesLinks?.slice(0, 5) ?? []

  return (
    <footer className="pb-mobile-nav desktop-nav:pb-0">
      <nav className="overflow-hidden bg-teal-5">
        <div className="md:mx-auto md:flex md:max-w-1440 md:divide-x md:divide-teal-4 md:px-48">
          {footerSiteLinks?.length ? (
            <div>
              <FooterHeading className="relative px-24 md:pl-0 md:pr-120">
                Acre
              </FooterHeading>
              <FooterList
                className={cx(
                  'relative px-24 md:pl-0 md:pr-32',
                  'before:absolute before:-top-px before:right-full before:block before:w-screen before:border-b before:border-teal-4'
                )}
              >
                {footerSiteLinks.map(({ key: text, value: url }, index) => (
                  <FooterListItem
                    key={text}
                    href={url}
                    label={
                      text === 'Careers' ? t('footer.we-are-hiring') : undefined
                    }
                    trackingPosition={isDesktop ? 'Left' : 'Middle'}
                    trackingPriority={index}
                  >
                    {text}
                  </FooterListItem>
                ))}
              </FooterList>
            </div>
          ) : null}
          {footerCurrentLocations?.length || footerFutureLocations?.length ? (
            <div>
              <FooterHeading className="px-24 md:pl-32 md:pr-120">
                {t('footer.homes')}
              </FooterHeading>
              <FooterList
                className={cx('px-24 md:px-32', {
                  'relative before:absolute before:-top-px before:left-full before:block before:w-screen before:border-b before:border-teal-4':
                    !showAdditionalLinks && !guidesLinks.length,
                })}
              >
                {footerCurrentLocations?.map(
                  ({ key: text, value: url }, index) => (
                    <FooterListItem
                      key={text}
                      href={url}
                      trackingPosition={isDesktop ? 'Left - Middle' : 'Middle'}
                      trackingPriority={
                        isDesktop
                          ? index
                          : index + (footerSiteLinks?.length ?? 0)
                      }
                    >
                      {text}
                    </FooterListItem>
                  )
                )}
                {footerFutureLocations?.map((futureLocation, index) => (
                  <FooterListItem
                    key={futureLocation}
                    trackingPosition={isDesktop ? 'Left - Middle' : 'Middle'}
                    trackingPriority={index}
                  >
                    {futureLocation} (coming soon)
                  </FooterListItem>
                ))}
              </FooterList>
            </div>
          ) : null}
          {guidesLinks.length ? (
            <div>
              <FooterHeading className="px-24 md:pl-32 md:pr-120">
                Guides
              </FooterHeading>
              <FooterList
                className={cx('px-24 md:px-32', {
                  'relative before:absolute before:-top-px before:left-full before:block before:w-screen before:border-b before:border-teal-4':
                    !showAdditionalLinks,
                })}
              >
                {guidesLinks.map(({ key: text, value: url }, index) => (
                  <FooterListItem
                    key={url}
                    href={url}
                    trackingPosition={isDesktop ? 'Left - Middle' : 'Middle'}
                    trackingPriority={index}
                  >
                    {text}
                  </FooterListItem>
                ))}
              </FooterList>
            </div>
          ) : null}
          {showAdditionalLinks ? (
            <div>
              <FooterHeading className="px-24 md:pl-32 md:pr-120">
                {footerAdditionalLinksHeading}
              </FooterHeading>
              <FooterList
                className={cx(
                  'px-24 md:px-32',
                  'relative before:absolute before:-top-px before:left-full before:block before:w-screen before:border-b before:border-teal-4'
                )}
              >
                {footerAdditionalLinks.map(
                  ({ key: text, value: url }, index) => (
                    <FooterListItem
                      key={text}
                      href={url}
                      trackingPosition={isDesktop ? 'Left - Middle' : 'Middle'}
                      trackingPriority={index}
                    >
                      {text}
                    </FooterListItem>
                  )
                )}
              </FooterList>
            </div>
          ) : null}
          <div>
            <FooterHeading className="px-24 md:pl-32 md:pr-120">
              Connect
            </FooterHeading>
            <div
              className={cx(
                'px-24 pt-10 text-14 leading-relaxed text-white rt-a:underline md:px-32',
                'border-t border-teal-4',
                'relative before:absolute before:-top-px before:left-full before:block before:w-screen before:border-b before:border-teal-4'
              )}
            >
              {footerConnectText
                ? documentToReactComponents(footerConnectText.json)
                : null}
            </div>
            <div className="px-24 pt-16 md:px-32">
              <button
                type="button"
                className="cky-banner-element text-14 text-white underline"
              >
                Do Not Sell or Share My Personal Information
              </button>
            </div>
            <ul className="flex items-center space-x-20 px-24 pb-24 pt-20 md:px-32 md:pb-32">
              <li>
                <a
                  className="block text-white"
                  target="_blank"
                  rel="noreferrer"
                  href="https://www.instagram.com/acre.homes/"
                >
                  <InstagramIcon
                    aria-hidden="true"
                    className="rect-footer-social"
                  />
                  <span className="sr-only">Instagram</span>
                </a>
              </li>
              <li>
                <a
                  className="block text-white"
                  target="_blank"
                  rel="noreferrer"
                  href="https://www.linkedin.com/company/acrehomes2021/"
                >
                  <LinkedInIcon
                    aria-hidden="true"
                    className="rect-footer-social"
                  />
                  <span className="sr-only">LinkedIn</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </footer>
  )
}

export default Footer
